import { DataObject, DataObjectFactory } from "@/v2/models/base-data-object.js";
import { Parameter } from "@/v2/models/parameters";

export class ShippingProvider extends DataObject {
    _type = "shipping_provider";

    id;
    name;
    type;
    pic;
    pic_full_name;
    vehicle_types = {};
}

export class ShippingVehicleType extends DataObject {
    _type = "shipping_vehicle_type";

    id;
    shipping_provider_id;
    name;
    capacity;
    volume;
    length;
    width;
    height;
    quantity;
    vehicles = {};
}

export class ShippingVehicle extends DataObject {
    _type = "shipping_vehicle";

    id;
    shipping_vehicle_type_id;
    vehicle_id;
    vehicle_name;
    location;
    default_driver;
}

export class ShippingProviderMapperParameter extends Parameter {
    shippingProviderMapper;
    shippingVehicleTypeMapperParameter = new ShippingVehicleTypeMapperParameter();
}

export class ShippingVehicleTypeMapperParameter extends Parameter {
    shippingVehicleTypeMapper = (shippingVehicleType) => {
        return {
            id: shippingVehicleType.id,
            shipping_provider_id: shippingVehicleType.shipping_provider_id,
            name: shippingVehicleType.name,
            capacity: shippingVehicleType.capacity,
            volume: shippingVehicleType.volume,
            length: shippingVehicleType.length,
            width: shippingVehicleType.width,
            height: shippingVehicleType.height,
            quantity: shippingVehicleType.quantity,
            vehicles: shippingVehicleType.vehicles,
        };
    };

    shippingVehicleMapperParameter = new ShippingVehicleMapperParameter();
}

export class ShippingVehicleMapperParameter extends Parameter {
    shippingVehicleMapper;
}

export class ShippingProviderFactory {
    /**
     * Creates a new instance of a DataObject.
     * @function
     * @static
     * @param {ShippingProviderMapperParameter} shippingProviderMapperParameter Parameter object containing mappers that the factory expects.
     * @param {Object, null} data Data to construct the ShippingProvider object with.
     * @returns {ShippingProvider}
     */
    static construct(shippingProviderMapperParameter, data = null) {
        if (data != null) {
            let shippingProvider = DataObjectFactory.getInstance().construct(
                ShippingProvider,
                data ? shippingProviderMapperParameter.shippingProviderMapper(data) : null
            );

            if (data.vehicle_types) {
                const vehicle_types = {};
                data.vehicle_types.forEach((vehicle_type) => {
                    vehicle_types[vehicle_type.id] = ShippingVehicleTypeFactory.construct(
                        shippingProviderMapperParameter.shippingVehicleTypeMapperParameter,
                        vehicle_type
                    );
                });
                shippingProvider.vehicle_types = vehicle_types;
            }

            return shippingProvider;
        }

        return null;
    }
}

export class ShippingVehicleTypeFactory {
    /**
     * Creates a new instance of a DataObject.
     * @function
     * @static
     * @param {ShippingVehicleTypeMapperParameter} shippingVehicleTypeMapperParameter Parameter object containing mappers that the factory expects.
     * @param {Object, null} data Data to construct the ShippingVehicleType object with.
     * @returns {ShippingVehicleType}
     */
    static construct(shippingVehicleTypeMapperParameter, data = null) {
        if (data != null) {
            let shippingVehicleType = DataObjectFactory.getInstance().construct(
                ShippingVehicleType,
                data ? shippingVehicleTypeMapperParameter.shippingVehicleTypeMapper(data) : null
            );

            if (data.vehicles) {
                const vehicles = {};

                let dataVehicles = Array.isArray(data.vehicles) ? data.vehicles : Object.values(data.vehicles);

                dataVehicles.forEach((vehicle) => {
                    vehicles[vehicle.id] = ShippingVehicleFactory.construct(
                        shippingVehicleTypeMapperParameter.shippingVehicleMapperParameter,
                        vehicle
                    );
                });
                shippingVehicleType.vehicles = vehicles;
            }

            return shippingVehicleType;
        }

        return null;
    }
}

export class ShippingVehicleFactory {
    /**
     * Creates a new instance of a DataObject.
     * @function
     * @static
     * @param {ShippingVehicleMapperParameter} shippingVehicleMapperParameter Parameter object containing mappers that the factory expects.
     * @param {Object, null} data Data to construct the ShippingVehicle object with.
     * @returns {ShippingVehicle}
     */
    static construct(shippingVehicleMapperParameter, data = null) {
        if (data != null) {
            let shippingVehicle = DataObjectFactory.getInstance().construct(
                ShippingVehicle,
                data ? shippingVehicleMapperParameter.shippingVehicleMapper(data) : null
            );

            return shippingVehicle;
        }

        return null;
    }
}
